import { RateAlert, NotificationMethod } from '@/types/currency'
import { NOTIFICATION_TYPES } from '@/utils/constants'

/**
 * Service for managing notifications and alerts
 */
export class NotificationService {
  private readonly vapidPublicKey: string | undefined
  private serviceWorkerRegistration: ServiceWorkerRegistration | null = null

  constructor() {
    this.vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY
    this.initializeServiceWorker()
  }

  /**
   * Initialize service worker for push notifications
   */
  private async initializeServiceWorker(): Promise<void> {
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      try {
        this.serviceWorkerRegistration = await navigator.serviceWorker.register('/sw.js')
        console.log('Service Worker registered successfully')
      } catch (error) {
        console.error('Service Worker registration failed:', error)
      }
    }
  }

  /**
   * Request permission for notifications
   */
  async requestPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      throw new Error('This browser does not support notifications')
    }

    const permission = await Notification.requestPermission()
    return permission
  }

  /**
   * Check if notifications are supported and permitted
   */
  isNotificationSupported(): boolean {
    return 'Notification' in window && Notification.permission === 'granted'
  }

  /**
   * Send rate alert notification
   */
  async sendAlertNotification(alert: RateAlert, currentRate: number): Promise<void> {
    const title = `Rate Alert: ${alert.fromCurrency}/${alert.toCurrency}`
    const message = `Rate ${alert.condition} ${alert.targetRate}. Current: ${currentRate.toFixed(4)}`

    // Send notifications through enabled methods
    for (const method of alert.notificationMethods) {
      if (method.enabled) {
        try {
          await this.sendNotification(method, title, message, {
            type: NOTIFICATION_TYPES.RATE_ALERT,
            alertId: alert.id,
            currencyPair: `${alert.fromCurrency}/${alert.toCurrency}`,
            currentRate,
            targetRate: alert.targetRate
          })
        } catch (error) {
          console.error(`Failed to send ${method.type} notification:`, error)
        }
      }
    }
  }

  /**
   * Send system notification
   */
  async sendSystemNotification(
    title: string,
    message: string,
    type: string = NOTIFICATION_TYPES.INFO
  ): Promise<void> {
    if (this.isNotificationSupported()) {
      await this.showBrowserNotification(title, message, {
        type,
        timestamp: Date.now()
      })
    }
  }

  /**
   * Send notification through specific method
   */
  private async sendNotification(
    method: NotificationMethod,
    title: string,
    message: string,
    data: any
  ): Promise<void> {
    switch (method.type) {
      case 'push':
        await this.sendPushNotification(title, message, data)
        break
      case 'email':
        await this.sendEmailNotification(method.target, title, message, data)
        break
      case 'sms':
        await this.sendSMSNotification(method.target, message, data)
        break
      case 'webhook':
        await this.sendWebhookNotification(method.target, title, message, data)
        break
      default:
        console.warn(`Unsupported notification method: ${method.type}`)
    }
  }

  /**
   * Send browser push notification
   */
  private async sendPushNotification(title: string, message: string, data: any): Promise<void> {
    if (!this.serviceWorkerRegistration) {
      throw new Error('Service Worker not registered')
    }

    // Subscribe to push service if not already subscribed
    const subscription = await this.subscribeToPush()
    
    if (subscription) {
      // For demo purposes, show local notification
      // In production, this would send to your push service
      await this.showBrowserNotification(title, message, data)
    }
  }

  /**
   * Subscribe to push notifications
   */
  private async subscribeToPush(): Promise<PushSubscription | null> {
    if (!this.serviceWorkerRegistration || !this.vapidPublicKey) {
      return null
    }

    try {
      const subscription = await this.serviceWorkerRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(this.vapidPublicKey)
      })

      // Send subscription to your server
      await this.sendSubscriptionToServer(subscription)
      
      return subscription
    } catch (error) {
      console.error('Failed to subscribe to push notifications:', error)
      return null
    }
  }

  /**
   * Show browser notification
   */
  private async showBrowserNotification(title: string, message: string, data: any): Promise<void> {
    if (!this.isNotificationSupported()) {
      return
    }

    const options: NotificationOptions = {
      body: message,
      icon: '/icons/icon-192x192.png',
      badge: '/icons/badge-72x72.png',
      data,
      tag: data.type || 'general',
      timestamp: Date.now(),
      requireInteraction: data.type === NOTIFICATION_TYPES.RATE_ALERT,
      actions: data.type === NOTIFICATION_TYPES.RATE_ALERT ? [
        {
          action: 'view',
          title: 'View Details',
          icon: '/icons/view-icon.png'
        },
        {
          action: 'dismiss',
          title: 'Dismiss',
          icon: '/icons/dismiss-icon.png'
        }
      ] : undefined
    }

    const notification = new Notification(title, options)

    notification.onclick = () => {
      // Handle notification click
      window.focus()
      notification.close()
      
      if (data.type === NOTIFICATION_TYPES.RATE_ALERT) {
        // Navigate to alerts page or specific alert
        window.location.hash = `#/alerts/${data.alertId}`
      }
    }

    notification.onclose = () => {
      console.log('Notification closed')
    }

    notification.onerror = (error) => {
      console.error('Notification error:', error)
    }
  }

  /**
   * Send email notification (would integrate with email service)
   */
  private async sendEmailNotification(
    email: string,
    title: string,
    message: string,
    data: any
  ): Promise<void> {
    // This would integrate with an email service like SendGrid, Mailgun, etc.
    console.log('Email notification (mock):', { email, title, message, data })
    
    // Example implementation:
    // const response = await fetch('/api/send-email', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify({ to: email, subject: title, body: message, data })
    // })
    
    // if (!response.ok) {
    //   throw new Error('Failed to send email notification')
    // }
  }

  /**
   * Send SMS notification (would integrate with SMS service)
   */
  private async sendSMSNotification(phone: string, message: string, data: any): Promise<void> {
    // This would integrate with an SMS service like Twilio, AWS SNS, etc.
    console.log('SMS notification (mock):', { phone, message, data })
    
    // Example implementation:
    // const response = await fetch('/api/send-sms', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify({ to: phone, message, data })
    // })
    
    // if (!response.ok) {
    //   throw new Error('Failed to send SMS notification')
    // }
  }

  /**
   * Send webhook notification
   */
  private async sendWebhookNotification(
    url: string,
    title: string,
    message: string,
    data: any
  ): Promise<void> {
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title,
          message,
          data,
          timestamp: Date.now()
        })
      })

      if (!response.ok) {
        throw new Error(`Webhook request failed: ${response.status} ${response.statusText}`)
      }
    } catch (error) {
      console.error('Webhook notification failed:', error)
      throw error
    }
  }

  /**
   * Send subscription to server
   */
  private async sendSubscriptionToServer(subscription: PushSubscription): Promise<void> {
    // This would send the subscription to your backend server
    console.log('Push subscription (mock):', subscription)
    
    // Example implementation:
    // const response = await fetch('/api/subscribe', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify(subscription)
    // })
    
    // if (!response.ok) {
    //   throw new Error('Failed to send subscription to server')
    // }
  }

  /**
   * Convert VAPID key to Uint8Array
   */
  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4)
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
    
    const rawData = window.atob(base64)
    const outputArray = new Uint8Array(rawData.length)
    
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i)
    }
    
    return outputArray
  }

  /**
   * Get notification settings
   */
  getNotificationSettings(): {
    supported: boolean
    permission: NotificationPermission
    pushSupported: boolean
    serviceWorkerReady: boolean
  } {
    return {
      supported: 'Notification' in window,
      permission: 'Notification' in window ? Notification.permission : 'denied',
      pushSupported: 'PushManager' in window && 'serviceWorker' in navigator,
      serviceWorkerReady: this.serviceWorkerRegistration !== null
    }
  }

  /**
   * Test notification
   */
  async testNotification(): Promise<void> {
    if (!this.isNotificationSupported()) {
      throw new Error('Notifications are not supported or not permitted')
    }

    await this.showBrowserNotification(
      'Test Notification',
      'This is a test notification from Currency Converter Pro',
      { type: NOTIFICATION_TYPES.INFO, test: true }
    )
  }
}